// Vite/Rollup cannot resolve a dynamic `import()` built from a variable, so the loadable set is
// enumerated at build time instead of being typed out as a switch over every moment locale.
// https://github.com/vitejs/vite/issues/14102
//
// Non-eager on purpose: each entry stays a lazy `() => import(...)` thunk, so a locale is its own
// chunk and loading one does not pull the other ~136 in. `{ eager: true }` here would execute every
// bundle at module load and register all of them with moment, which `localeGlobals.test.ts` pins.
const localeBundleLoaders = import.meta.glob('/node_modules/moment/dist/locale/*.js');

const BUNDLE_PATH_PREFIX = '/node_modules/moment/dist/locale/';

/**
 * `locale code -> () => import(bundle)` for every moment locale bundle that ships, e.g. `nl`,
 * `nl-be`, `zh-cn`. Exported so the organization Locale select and this module's tests read the same
 * source rather than each keeping their own idea of what moment supports.
 */
export const momentLocaleBundleLoaders = new Map(
  Object.entries(localeBundleLoaders).map(([path, load]) => [
    path.slice(BUNDLE_PATH_PREFIX.length, -'.js'.length),
    load,
  ])
);

const languageOf = (code) => code.split('-')[0];

// moment systematically omits a language's home-territory variant and expects the bare language
// bundle to cover it: it ships `nl` and `nl-be` but no `nl-nl`, `de`/`de-at`/`de-ch` but no `de-de`,
// `fr`/`fr-ca`/`fr-ch` but no `fr-fr`, `es`/`es-do`/`es-mx`/`es-us` but no `es-es`. Falling back from
// `xx-yy` to `xx` therefore covers every one of those, present and future, rather than one at a time.
// moment's own chooseLocale trims the region off an unregistered tag, so the subsequent
// `moment.locale('nl-nl')` resolves to the `nl` data this loaded.
const bundleLoaderFor = (code) =>
  momentLocaleBundleLoaders.get(code) || momentLocaleBundleLoaders.get(languageOf(code)) || null;

// English is moment's built-in locale, so it ships no bundle at all — no `en.js` and no `en-us.js`.
// A rule of "xx-yy.js or xx.js exists" would therefore drop `en-us`, the most common locale in
// production, so English is loadable by having nothing to load.
const isMomentBuiltInLanguage = (code) => languageOf(code) === 'en';

/**
 * Whether moment can render dates for this locale — either a bundle resolves for it, or it is
 * English, which needs none.
 */
export function isMomentLocaleLoadable(localeCode) {
  const code = localeCode.toLowerCase();
  return isMomentBuiltInLanguage(code) || bundleLoaderFor(code) !== null;
}

/**
 * Register the moment locale data for `localeCode`, throwing if moment has none. Callers set
 * `moment.locale(...)` themselves — this only makes the data available for that call to find.
 */
export async function importLocaleModule(localeCode) {
  const code = localeCode.toLowerCase();

  const load = bundleLoaderFor(code);
  if (load) {
    await load();
    return;
  }

  if (isMomentBuiltInLanguage(code)) {
    return;
  }

  throw new Error(`Locale "${localeCode}" is not supported.`);
}
